/**
 * 推荐列表 API
 *
 * 获取首页推荐内容, 支持按需加载内容详情
 */

import type {
    APIRecommendation,
    APIRecommendationResponse,
    RecommendItem,
    RecommendationResponse,
} from '@/utils/type';
import { isDev } from '@/utils/env';
import { getDevMockRecommendationResponse } from '@/mocks/devHomeApiMocks';
import { apiFetcher } from '../fetcher';
import { fetchContent } from './content';

/**
 * 解析推荐列表, 按需获取内容详情
 *
 * @param withContent - 是否需要内容详情
 * @param feeds - 原始推荐列表
 *
 * 流程:
 * 1. 不需要详情: 直接转换格式, contentList 置空
 * 2. 需要详情: 并行请求所有内容, 过滤失败项
 */
const parseFeeds = async (
    withContent: boolean,
    feeds: APIRecommendation[],
): Promise<RecommendItem[]> => {
    // 不需要内容详情, 直接返回
    if (!withContent) {
        return feeds.map(({ content, ...rest }) => ({
            ...rest,
            contentList: [],
        }));
    }

    // 并行请求所有内容详情
    const results = await Promise.all(
        feeds.map(async (feed) => {
            // 无 contentId 则跳过
            if (!feed.contentId) return null;

            // 请求内容详情
            const data = await fetchContent(feed.contentId);
            if (!data) return null;

            // 转换格式: 移除 content 字段, 添加 contentList
            const { content, ...rest } = feed;
            return { ...rest, contentList: data.contentList };
        }),
    );

    // 过滤失败项
    return results.filter((item): item is RecommendItem => item !== null);
};

/**
 * 获取推荐列表
 *
 * @param data.contentTextSwitch - 是否需要内容详情
 * @param data.issuedList - 已展示的内容 ID (用于去重)
 * @param data.transparent - 服务端透传字段 (用于分页)
 * @param data.categoryId - 分类 ID
 */
export const fetchRecommendation = async (data: {
    contentTextSwitch: boolean;
    issuedList: string[];
    transparent: string;
    categoryId: string;
}): Promise<RecommendationResponse | null> => {
    if (isDev) {
        return getDevMockRecommendationResponse({
            contentTextSwitch: data.contentTextSwitch,
            categoryId: data.categoryId,
        });
    }

    try {
        // Step 1: 请求推荐列表
        const res = await apiFetcher.post<APIRecommendationResponse>(
            '/themeres/magazine/detail/home',
            data,
        );

        // Step 2: 处理业务状态码
        if (res.code === 201) return null; // 到达推荐上限
        if (res.code !== 200) {
            console.error('API error:', res);
            return null;
        }

        // Step 3: 解析并返回
        const { feedsList, issuedListMaxSize, transparent } =
            res.detailRecommendDataDto;
        return {
            feedsList: await parseFeeds(data.contentTextSwitch, feedsList),
            issuedListMaxSize,
            transparent,
        };
    } catch (err) {
        console.error('Error fetching recommendation:', err);
        return null;
    }
};
